import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
} from 'react';
import { PiCaretDown, PiHeadsetLight, PiUserLight } from 'react-icons/pi';
import { useCardFooterTagVisibility } from 'src/modules/communications/hooks/use-card-footer-tag-visibility';
import { useCardFooterResponsiveFocus } from 'src/modules/communications/hooks/use-card-footer-responsive-focus';
import type { ConversationCardFooterProps } from 'src/modules/communications/types/conversation-card.type';
import { getConversationFooterTags } from 'src/modules/communications/utils/conversation-footer-tags.util';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { HorizontalOverflowStrip } from 'src/modules/shared/components/molecules/horizontal-overflow-strip';
import { LabelList } from 'src/modules/shared/components/molecules/label-list';
import { Popover } from 'src/modules/shared/components/molecules/popovers/popover';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { mergeRefs } from 'src/modules/shared/utils/refs.util';
import { classes } from 'src/modules/shared/utils/styles.util';
import { ConversationFooterTagBadge } from './conversation-footer-tag-badge';

/**
 * Shows scrollable mobile badges or fitting desktop badges beside fixed assignment.
 * @param props - Existing badge conditions, current status, and assignee label.
 * @returns The regular card footer with an accessible, isolated tag popover.
 */
export const ConversationCardFooter: FC<ConversationCardFooterProps> = ({
  isTestConversation,
  status,
  isArchived,
  replyNeeded,
  assignedUserName,
}) => {
  const { t } = useI18n();
  const isMobile = useMediaQuery('(max-width: 639px)');
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const tagGroupRef = useRef<HTMLDivElement>(null);
  const responsiveFocus = useCardFooterResponsiveFocus(
    isMobile,
    mobileScrollRef,
    tagGroupRef
  );
  const [requestedOpen, setRequestedOpen] = useState(false);
  const tags = getConversationFooterTags(
    { isTestConversation, status, isArchived, replyNeeded },
    t('communications.testConversation.badge')
  );
  const tagGroupLabel =
    tags.length > 0 ? t('communications.conversationCard.tags') : undefined;
  const labels = tags.map(({ label }) => label).join('|');
  const { ref, isOverflowing, visibleIndexes, toggleLeft } =
    useCardFooterTagVisibility(labels, triggerRef, !isMobile);
  const measuredGroupRef = useMemo(() => mergeRefs(ref, tagGroupRef), [ref]);
  const open = requestedOpen && isOverflowing;
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setRequestedOpen(nextOpen);
      if (
        !nextOpen &&
        (document.getElementById(panelId)?.contains(document.activeElement) ||
          document.activeElement === document.body)
      ) {
        triggerRef.current?.focus();
      }
    },
    [panelId]
  );
  useEffect(() => {
    if (!isOverflowing) setRequestedOpen(false);
  }, [isOverflowing]);
  useEffect(() => {
    if (!open) return;
    // This Floating UI version resets its inside-press flag in a document
    // bubble listener, which required card/drag isolation prevents reaching.
    const handleOutsidePress = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        triggerRef.current?.contains(target) ||
        document.getElementById(panelId)?.contains(target)
      ) {
        return;
      }
      handleOpenChange(false);
    };
    document.addEventListener('pointerdown', handleOutsidePress, true);
    return () =>
      document.removeEventListener('pointerdown', handleOutsidePress, true);
  }, [open, handleOpenChange, panelId]);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleOpenChange(false);
      triggerRef.current?.focus();
    }
    event.stopPropagation();
  };
  const hiddenTags = tags.filter((_, index) => !visibleIndexes.includes(index));
  const hiddenCount = hiddenTags.length;
  const listItems = hiddenTags.map((tag) => ({
    ...tag,
    icon:
      tag.key === 'test' ? <PiHeadsetLight className="h-4 w-4" /> : undefined,
  }));
  const toggleLabel = t(
    hiddenCount === 1
      ? 'communications.conversationCard.tags.more.one'
      : 'communications.conversationCard.tags.more.many',
    { count: String(hiddenCount) }
  );

  return (
    <div
      {...responsiveFocus}
      className="mt-[7px] h-[22px] min-h-[22px] min-w-0 flex items-center gap-2"
    >
      <div
        ref={measuredGroupRef}
        role={tags.length > 0 ? 'group' : undefined}
        aria-label={tagGroupLabel}
        tabIndex={tags.length > 0 ? -1 : undefined}
        onKeyDown={(event) => event.stopPropagation()}
        className="relative min-w-0 flex-1 overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-secondary-500"
      >
        {isMobile ? (
          <div
            role="presentation"
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <HorizontalOverflowStrip
              scrollRef={mobileScrollRef}
              ariaLabel={tagGroupLabel}
              watchValue={labels}
              contentClassName="overscroll-x-contain"
            >
              <div className="flex w-max shrink-0 items-center gap-2 px-px py-px">
                {tags.map((tag) => (
                  <div key={tag.key} className="shrink-0">
                    <ConversationFooterTagBadge tag={tag} />
                  </div>
                ))}
              </div>
            </HorizontalOverflowStrip>
          </div>
        ) : (
          <>
            <div className="flex w-max items-center gap-2 px-px py-px">
              {tags.map((tag, index) => {
                const isVisible = visibleIndexes.includes(index);
                return (
                  <div
                    key={tag.key}
                    className={classes(
                      'shrink-0',
                      isVisible ? 'visible order-0' : 'invisible order-1'
                    )}
                    aria-hidden={!isVisible}
                  >
                    <ConversationFooterTagBadge tag={tag} />
                  </div>
                );
              })}
            </div>
            <div
              className={classes(
                'absolute top-1/2 flex -translate-y-1/2',
                isOverflowing ? 'visible' : 'invisible'
              )}
              aria-hidden={!isOverflowing}
              style={{
                left: toggleLeft,
              }}
              role="presentation"
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              onKeyDown={handleKeyDown}
              onClick={(event) => event.stopPropagation()}
            >
              <Popover
                open={open}
                onOpenChange={handleOpenChange}
                placement="top-start"
                className="!flex"
                contentClassName="w-[270px] max-w-[calc(100vw-2rem)] max-h-80 overflow-y-auto rounded-[9px] border-slate-200 bg-white text-slate-900 shadow-lg"
                contentId={panelId}
                contentAriaLabel={t('communications.conversationCard.tags')}
                handler={
                  <Button
                    ref={triggerRef}
                    type="button"
                    size="2xs"
                    variant="neutral"
                    appearance="outline"
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    tabIndex={isOverflowing ? 0 : -1}
                    aria-expanded={open}
                    aria-controls={open ? panelId : undefined}
                    aria-haspopup="dialog"
                    className="!h-auto !min-h-0 !gap-1 !rounded-full !px-1.5 !py-[0.095rem] focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-[-2px] focus-visible:!outline-secondary-500"
                  >
                    <Text
                      as="span"
                      disableAutoLink
                      className="m-0 flex h-fit items-center whitespace-nowrap text-2xs font-medium leading-[15px] tabular-nums"
                    >
                      +{hiddenCount}
                    </Text>
                    <PiCaretDown
                      className={classes('h-3 w-3', open && 'rotate-180')}
                      aria-hidden="true"
                    />
                  </Button>
                }
              >
                <div
                  role="presentation"
                  onPointerDown={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                  onKeyDown={handleKeyDown}
                  onClick={(event) => event.stopPropagation()}
                >
                  <LabelList
                    caption={t('communications.conversationCard.tags.other')}
                    items={listItems}
                  />
                </div>
              </Popover>
            </div>
          </>
        )}
      </div>

      <div
        className="min-w-0 max-w-[36%] [@container(min-width:520px)]:max-w-[24%] ml-auto flex shrink items-center gap-0.5 text-secondary-600"
        title={assignedUserName}
      >
        <PiUserLight className="h-3 w-3 shrink-0" />
        <Text
          as="span"
          size="tiny"
          disableAutoLink
          className="min-w-0 truncate text-[11px]"
        >
          {assignedUserName}
        </Text>
      </div>
    </div>
  );
};
